'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState, type FormEvent } from 'react'
import { ApiError } from '@ideight/api-client'
import { authClient } from '../../lib/auth-client'
import { api } from '../../lib/api'

/**
 * Vendor sign-up — a furniture / lighting / decor shop creates a vendor
 * account in one step. Signs the user up (Better Auth), onboards them as
 * a vendor (creates the vendor org + 3-month freemium + 10 free
 * generations), sets that org active, then lands on the dashboard.
 *
 * Public surface — does NOT wrap in AppShell (the user is not signed in
 * yet). Mirrors the editorial landing page palette so the visual
 * handoff from /landing-v1 to /vendor doesn't feel like another app.
 */
export default function VendorSignUpPage() {
  const router = useRouter()
  const [shopName, setShopName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const signUp = await authClient.signUp.email({ email, password, name: shopName })
      if (signUp.error) {
        setError(signUp.error.message ?? 'Sign up failed.')
        return
      }
      const profile = await api.vendorOnboard({
        shopName,
        contactEmail: contactEmail || email,
        ...(websiteUrl ? { websiteUrl } : {}),
      })
      await authClient.organization.setActive({ organizationId: profile.organizationId })
      router.push('/vendor/dashboard')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not complete vendor sign-up.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main style={pageStyle}>
      <p style={eyebrowStyle}>For vendors</p>
      <h1 style={titleStyle}>Become a vendor</h1>
      <p style={ledeStyle}>
        List your products in the Tinkerspace catalogue. Start with{' '}
        <strong style={{ color: 'var(--tks-ink)' }}>10 free 3D generations</strong> and three months
        free.
      </p>
      <form onSubmit={handleSubmit} style={formStyle}>
        <label style={labelStyle}>
          Shop name
          <input
            type="text"
            value={shopName}
            onChange={(e) => setShopName(e.target.value)}
            required
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Contact email
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="Shown on your storefront"
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Website (optional)
          <input
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://"
            style={inputStyle}
          />
        </label>
        <hr style={dividerStyle} />
        <label style={labelStyle}>
          Login email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            style={inputStyle}
          />
          <span style={hintStyle}>At least 8 characters.</span>
        </label>
        {error ? <p style={errorStyle}>{error}</p> : null}
        <button type="submit" disabled={submitting} style={buttonStyle}>
          {submitting ? 'Setting up your shop…' : 'Create vendor account'}
        </button>
      </form>
      <p style={footStyle}>
        Designing instead?{' '}
        <Link href="/sign-up" style={footLinkStyle}>
          Create a designer account
        </Link>
      </p>
    </main>
  )
}

const pageStyle: React.CSSProperties = {
  padding: '64px 32px 80px',
  maxWidth: 520,
  margin: '0 auto',
  color: 'var(--tks-ink)',
}

const eyebrowStyle: React.CSSProperties = {
  margin: '0 0 16px',
  fontSize: 12,
  letterSpacing: 2.6,
  textTransform: 'uppercase',
  color: 'var(--tks-accent)',
  fontWeight: 600,
}

const titleStyle: React.CSSProperties = {
  margin: '0 0 14px',
  fontSize: 42,
  fontWeight: 600,
  letterSpacing: -1,
  lineHeight: 1.05,
}

const ledeStyle: React.CSSProperties = {
  margin: '0 0 32px',
  color: 'var(--tks-ink-soft)',
  fontSize: 16,
  lineHeight: 1.6,
}

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 13,
  letterSpacing: 0.2,
  color: 'var(--tks-ink-soft)',
}

const inputStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 15,
  border: '1px solid var(--tks-line-strong)',
  borderRadius: 999,
  background: 'var(--tks-bg-elev)',
  color: 'var(--tks-ink)',
  fontFamily: 'inherit',
  outline: 'none',
}

const dividerStyle: React.CSSProperties = {
  border: 0,
  borderTop: '1px solid var(--tks-line)',
  width: '100%',
  margin: '4px 0',
}

const buttonStyle: React.CSSProperties = {
  marginTop: 8,
  padding: '14px 22px',
  fontSize: 15,
  fontWeight: 600,
  borderRadius: 999,
  border: 'none',
  background: 'var(--tks-accent)',
  color: 'var(--tks-accent-ink)',
  cursor: 'pointer',
  letterSpacing: 0.2,
}

const hintStyle: React.CSSProperties = {
  color: 'var(--tks-ink-faint)',
  fontSize: 12,
}

const errorStyle: React.CSSProperties = {
  color: '#ff7676',
  fontSize: 13,
  margin: 0,
}

const footStyle: React.CSSProperties = {
  color: 'var(--tks-ink-soft)',
  marginTop: 28,
  fontSize: 14,
}

const footLinkStyle: React.CSSProperties = {
  color: 'var(--tks-accent)',
  textDecoration: 'none',
}
